import { Component, OnInit, OnDestroy } from '@angular/core';
import { User } from '../../../../auth/user.model';
import { AdminService } from '../../../admin.service';
import { ActivatedRoute } from '@angular/router';
import { NotificationMessageService } from '../../../../shared/notification-message/notification-message.service';
import { NavigationService } from '../../../../shared/services/navigation.service';
import { BreadcrumbService } from '../../../../shared/breadcrumb/breadcrumb.service';
import { UserService } from '../../../../auth/user.service';

@Component({
	selector: 'app-user-edit',
	templateUrl: './user-edit.component.html',
	styleUrls: ['./user-edit.component.css']
})
export class UserEditComponent implements OnInit, OnDestroy {

	user: any;
	roles: any;
	dealers: any;
	truckDealers: any;
	oems: any;
	customers: any;
	isSsoUser: boolean = false;
	passwordValidationErrors: string[] = [];
	forbiddenWords: string[] = ['act', 'motive', 'actview', 'battview', 'actlink', 'motiveenergy'];

	constructor(
		private adminService: AdminService,
		private notificationMessage: NotificationMessageService,
		private route: ActivatedRoute,
		private navigation: NavigationService,
		private breadcrumb: BreadcrumbService,
		private userService: UserService
	) { }

	ngOnInit() {
		this.user = new User();
		this.user.id = +(this.route.snapshot.params.userId);
		this.user.roleid = null;

		this.getRoles();
		this.getCustomers();
		this.getDealers();
		this.getTruckDealers();
		this.getOEMs();

		if(this.user.id != 0)
			this.getUsers();

		this.breadcrumb.setBreadcrumb([
			{label: "Users", url: "/users"},
			{label: "Edit", url: ""}
		]);
	}
	ngOnDestroy() {
		this.breadcrumb.hide();
	}

	getUsers(){
		this.adminService.getUsers(this.user.id, this.user.id != 0).subscribe(
			(response: {user: any}) => {
				this.user = response.user;
				// Check if the user being edited is an SSO user
				// We can only detect SSO users who are currently logged in via SSO
				// This is because sessionType is only available in the current session
				const currentUser = this.userService.getCurrentUser();
				if(currentUser && currentUser.id === this.user.id) {
					this.isSsoUser = this.userService.isSSO();
				}
				// Note: If the user being edited is not the current user, we cannot determine
				// their SSO status from the session. The warning message explains this limitation.
			}
		);
	}

	saveUser(){
		// Validate password if it's being changed
		if(this.user.password) {
			this.validatePasswordPolicy();
			if(this.passwordValidationErrors.length > 0) {
				this.notificationMessage.setMessage(this.passwordValidationErrors.join('\n'), 'danger', 10000, true);
				return;
			}
		}

		this.adminService.saveUser(this.user).subscribe(
			(response: any) => {
				if(response && response.error) {
					this.notificationMessage.setMessage(response.error, 'danger', 10000, true);
				} else {
					this.notificationMessage.setMessage('globalSuccessMsg', 'success', 10000, true);
					this.goBack();
				}
			},
			(error: any) => {
				// Handle HTTP errors
				if(error.status === 404) {
					this.notificationMessage.setMessage('An error occurred while saving the user.', 'danger', 10000, true);
				} else {
					this.notificationMessage.setMessage('An error occurred: ' + (error.message || 'Unknown error'), 'danger', 10000, true);
				}
			}
		);
	}

	// AW-50: Client-side password policy validation
	validatePasswordPolicy() {
		const errors: string[] = [];
		const password = this.user.password;

		if (!password) {
			this.passwordValidationErrors = [];
			return;
		}

		// 1. Check minimum length (12 characters)
		if (password.length < 12) {
			errors.push('Password must be at least 12 characters long');
		}

		// 2. Check for at least one uppercase letter
		if (!/[A-Z]/.test(password)) {
			errors.push('Password must contain at least one uppercase letter');
		}

		// 3. Check for at least one lowercase letter
		if (!/[a-z]/.test(password)) {
			errors.push('Password must contain at least one lowercase letter');
		}

		// 4. Check for at least one digit
		if (!/[0-9]/.test(password)) {
			errors.push('Password must contain at least one digit');
		}

		// 5. Check for at least one special character
		if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(password)) {
			errors.push('Password must contain at least one special character');
		}

		// 6. Check for forbidden words (case-insensitive)
		const lowerPassword = password.toLowerCase();
		for (const word of this.forbiddenWords) {
			if (lowerPassword.includes(word.toLowerCase())) {
				errors.push(`Password cannot contain company-related words like "${word}"`);
				break; // Only report once
			}
		}

		this.passwordValidationErrors = errors;
	}

	isPasswordRequired(){
		return !+(this.user.id);
	}

	getRoles(){
		this.adminService.getRoles().subscribe(
			response => {
				this.roles = response;
				this.roles.unshift({id: 0, name:"Select Role"});
			}
		);
	};

	getCustomers() {
		this.adminService.getCustomers().subscribe(
			(response: {list: any}) => {
				this.customers = response.list;
				this.customers.unshift({id: 0, name:"Select Company"});
			}
		);
	};

	getDealers() {
		this.adminService.getDealers().subscribe(
			(response: {dealers: any}) => {
				this.dealers = response.dealers;
				this.dealers.unshift({id: 0, name:"Select a Dealer"});
			}
		);
	};

	getTruckDealers() {
		this.adminService.getDealers(0, 'truck_dealer').subscribe(
			(response: {dealers: any}) => {
				this.truckDealers = response.dealers;
				this.truckDealers.unshift({id: 0, name:"Select a Service Dealer Secondary"});
			}
		);
	};

	getOEMs() {
		this.adminService.getOEMs().subscribe(
			response => {
				this.oems = response;
				this.oems.unshift({id: 0, name:"Select a Battery OEM"});
			}
		);
	};

	goBack(){
		this.navigation.goBack(['/users']);
	}
}
